import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../../../components/Header';
import Loader from '../../../components/Loader/Loader';
import { getUserByID } from '../../../services/userService';

interface UserDetailsData {
  id: number;
  name?: string;
  email?: string;
  user_code?: string | null;
  role?: string | null;
  shift?: string | null;
  phone?: string | null;
  mobile?: string | null;
  mobile_no?: string | null;
  address?: string | null;
  street?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  profile_photo?: string | null;
  profile_image?: string | null;
  department?: { name?: string } | null;
  user_type?: { title?: string } | null;
}

const DetailItem = ({ label, value }: { label: string; value?: string | number | null }) => (
  <div>
    <p className="mb-1 text-sm text-gray-500">{label}</p>
    <p className="break-words text-sm font-medium text-gray-900">{value || '—'}</p>
  </div>
);

const UserDetails = () => {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<UserDetailsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      if (!id) {
        setError('User ID is missing.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await getUserByID(id);
        setUser(response?.data || response);
      } catch (err: any) {
        console.error('Error fetching user details:', err);
        setError(err?.response?.data?.message || err?.message || 'Failed to load user details.');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  const phone = user?.mobile_no || user?.mobile || user?.phone;
  const role = user?.user_type?.title || user?.role;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <Header title="User Details" showBackButton={true} />

      <div className="flex-1 overflow-auto bg-white p-6">
        {loading ? (
          <Loader />
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-white p-8 text-center text-sm text-red-600">{error}</div>
        ) : !user ? (
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">User not found.</div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-slate-900">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">{user.id}</h2>
            </div>

            <section className="mb-6">
              <h3 className="mb-3 font-semibold text-blue-600">Info</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <DetailItem label="User Name" value={user.name} />
                <DetailItem label="Email" value={user.email} />
                <DetailItem label="Mobile No" value={phone} />
                <DetailItem label="Department" value={user.department?.name} />
              </div>
            </section>

            <section className="mb-6">
              <h3 className="mb-3 font-semibold text-blue-600">Contact Details</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <DetailItem label="Address" value={user.address} />
                <DetailItem label="Street" value={user.street} />
                <DetailItem label="City" value={user.city} />
                <DetailItem label="State" value={user.state} />
                <DetailItem label="Country" value={user.country} />
              </div>
            </section>

            <section>
              <h3 className="mb-3 font-semibold text-blue-600">Role</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <DetailItem label="Role" value={role} />
                <DetailItem label="Shift" value={user.shift} />
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDetails;
